/**
 * DrawReady Role & Permission System
 * Central configuration for team roles and their permissions
 */

// Role definitions with metadata
export const ROLES = {
  admin: {
    id: 'admin',
    name: 'Admin',
    description: 'Full access to all features including draws, payments, and team management',
    icon: 'ShieldCheck',
    color: {
      bg: 'bg-slate-900',
      text: 'text-slate-50',
      border: 'border-slate-700',
      badge: 'bg-slate-900 text-white'
    },
    canDo: [
      'Full control over all projects',
      'Request draws from lenders',
      'Record all payments',
      'Manage team members',
      'Access billing & subscription',
      'View all reports & 1099s'
    ],
    cannotDo: []
  },
  project_manager: {
    id: 'project_manager',
    name: 'Project Manager',
    description: 'Manage assigned projects including phases, bids, contractors, and schedules',
    icon: 'Briefcase',
    color: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      badge: 'bg-blue-100 text-blue-700'
    },
    canDo: [
      'Manage phases & schedules',
      'Create & send bid requests',
      'Award contracts to contractors',
      'Upload receipts & documents',
      'Manage contractors',
      'Generate project reports'
    ],
    cannotDo: [
      'Request draws',
      'Record payments',
      'Manage team members',
      'Access billing/subscription',
      'View 1099/tax documents'
    ],
    requiresProjectAssignment: true
  },
  accountant: {
    id: 'accountant',
    name: 'Accountant',
    description: 'View-only access to financials, reports, and tax documents across all projects',
    icon: 'Calculator',
    color: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      badge: 'bg-emerald-100 text-emerald-700'
    },
    canDo: [
      'View all project financials',
      'Generate financial reports',
      'View & export 1099 documents',
      'View payment history',
      'View receipts & invoices'
    ],
    cannotDo: [
      'Edit any project data',
      'Request draws',
      'Record payments',
      'Manage bids or contractors',
      'Manage team members'
    ],
    requiresProjectAssignment: false
  },
  investor: {
    id: 'investor',
    name: 'Investor',
    description: 'Read-only dashboard access to view project progress and high-level financials',
    icon: 'TrendingUp',
    color: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-700'
    },
    canDo: [
      'View project dashboards',
      'View budget summaries',
      'View progress reports',
      'View draw history'
    ],
    cannotDo: [
      'Edit any data',
      'View detailed financials',
      'View 1099/tax documents',
      'View contractor details',
      'Access any admin features'
    ],
    requiresProjectAssignment: false
  }
};

// Permission definitions
export const PERMISSIONS = {
  // Admin-only permissions
  REQUEST_DRAWS: 'request_draws',
  RECORD_PAYMENTS: 'record_payments',
  MANAGE_TEAM: 'manage_team',
  MANAGE_BILLING: 'manage_billing',
  
  // Project management permissions
  VIEW_PROJECTS: 'view_projects',
  EDIT_PROJECTS: 'edit_projects',
  MANAGE_PHASES: 'manage_phases',
  CREATE_BIDS: 'create_bids',
  AWARD_CONTRACTS: 'award_contracts',
  UPLOAD_RECEIPTS: 'upload_receipts',
  MANAGE_CONTRACTORS: 'manage_contractors',
  UPDATE_SCHEDULES: 'update_schedules',
  
  // Financial permissions
  VIEW_FINANCIALS: 'view_financials',
  GENERATE_REPORTS: 'generate_reports',
  VIEW_TAX_DOCS: 'view_tax_docs'
};

// Role-Permission matrix
export const ROLE_PERMISSIONS = {
  admin: [
    PERMISSIONS.REQUEST_DRAWS,
    PERMISSIONS.RECORD_PAYMENTS,
    PERMISSIONS.MANAGE_TEAM,
    PERMISSIONS.MANAGE_BILLING,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.MANAGE_PHASES,
    PERMISSIONS.CREATE_BIDS,
    PERMISSIONS.AWARD_CONTRACTS,
    PERMISSIONS.UPLOAD_RECEIPTS,
    PERMISSIONS.MANAGE_CONTRACTORS,
    PERMISSIONS.UPDATE_SCHEDULES,
    PERMISSIONS.VIEW_FINANCIALS,
    PERMISSIONS.GENERATE_REPORTS,
    PERMISSIONS.VIEW_TAX_DOCS
  ],
  project_manager: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.MANAGE_PHASES,
    PERMISSIONS.CREATE_BIDS,
    PERMISSIONS.AWARD_CONTRACTS,
    PERMISSIONS.UPLOAD_RECEIPTS,
    PERMISSIONS.MANAGE_CONTRACTORS,
    PERMISSIONS.UPDATE_SCHEDULES,
    PERMISSIONS.VIEW_FINANCIALS,
    PERMISSIONS.GENERATE_REPORTS
  ],
  accountant: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_FINANCIALS,
    PERMISSIONS.GENERATE_REPORTS,
    PERMISSIONS.VIEW_TAX_DOCS
  ],
  investor: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_FINANCIALS,
    PERMISSIONS.GENERATE_REPORTS
  ]
};

// Permission labels for the guide modal
export const PERMISSION_LABELS = {
  [PERMISSIONS.REQUEST_DRAWS]: 'Request Draws',
  [PERMISSIONS.RECORD_PAYMENTS]: 'Record Payments',
  [PERMISSIONS.MANAGE_TEAM]: 'Manage Team',
  [PERMISSIONS.MANAGE_BILLING]: 'Billing & Subscription',
  [PERMISSIONS.VIEW_PROJECTS]: 'View Projects',
  [PERMISSIONS.EDIT_PROJECTS]: 'Edit Projects',
  [PERMISSIONS.MANAGE_PHASES]: 'Manage Phases',
  [PERMISSIONS.CREATE_BIDS]: 'Create/Send Bids',
  [PERMISSIONS.AWARD_CONTRACTS]: 'Award Contracts',
  [PERMISSIONS.UPLOAD_RECEIPTS]: 'Upload Receipts',
  [PERMISSIONS.MANAGE_CONTRACTORS]: 'Manage Contractors',
  [PERMISSIONS.UPDATE_SCHEDULES]: 'Update Schedules',
  [PERMISSIONS.VIEW_FINANCIALS]: 'View Financials',
  [PERMISSIONS.GENERATE_REPORTS]: 'Generate Reports',
  [PERMISSIONS.VIEW_TAX_DOCS]: 'View 1099/Tax Docs'
};

// Permission categories for organized display
export const PERMISSION_CATEGORIES = [
  {
    name: 'Admin Only',
    permissions: [
      PERMISSIONS.REQUEST_DRAWS,
      PERMISSIONS.RECORD_PAYMENTS,
      PERMISSIONS.MANAGE_TEAM,
      PERMISSIONS.MANAGE_BILLING
    ]
  },
  {
    name: 'Project Management',
    permissions: [
      PERMISSIONS.VIEW_PROJECTS,
      PERMISSIONS.EDIT_PROJECTS,
      PERMISSIONS.MANAGE_PHASES,
      PERMISSIONS.CREATE_BIDS,
      PERMISSIONS.AWARD_CONTRACTS,
      PERMISSIONS.UPLOAD_RECEIPTS,
      PERMISSIONS.MANAGE_CONTRACTORS,
      PERMISSIONS.UPDATE_SCHEDULES
    ]
  },
  {
    name: 'Financial & Reports',
    permissions: [
      PERMISSIONS.VIEW_FINANCIALS,
      PERMISSIONS.GENERATE_REPORTS,
      PERMISSIONS.VIEW_TAX_DOCS
    ]
  }
];

/**
 * Check if a role has a specific permission
 * @param {string|string[]} roles - Single role or array of roles
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export function hasPermission(roles, permission) {
  if (!roles) return false;
  const roleArray = Array.isArray(roles) ? roles : [roles];
  return roleArray.some(role => ROLE_PERMISSIONS[role]?.includes(permission));
}

/**
 * Check if user has access to a specific project
 * @param {object} teamMember - Team member object with roles and assigned_projects
 * @param {string} projectId - Project ID to check
 * @returns {boolean}
 */
export function hasProjectAccess(teamMember, projectId) {
  if (!teamMember) return false;
  
  const roles = teamMember.roles || [teamMember.role];
  
  // Admin and non-project-specific roles have access to all projects
  if (roles.includes('admin') || roles.includes('accountant') || roles.includes('investor')) {
    return true;
  }
  
  // Project managers need explicit assignment
  if (roles.includes('project_manager')) {
    const assignedProjects = teamMember.assigned_projects || [];
    return assignedProjects.includes(projectId);
  }
  
  return false;
}

/**
 * Get all permissions for a user based on their roles
 * @param {string|string[]} roles - Single role or array of roles
 * @returns {string[]} Array of permission IDs
 */
export function getUserPermissions(roles) {
  if (!roles) return [];
  const roleArray = Array.isArray(roles) ? roles : [roles];
  const permissions = new Set();
  
  roleArray.forEach(role => {
    const rolePerms = ROLE_PERMISSIONS[role] || [];
    rolePerms.forEach(p => permissions.add(p));
  });
  
  return Array.from(permissions);
}

/**
 * Check if a role requires project assignment
 * @param {string} role - Role ID
 * @returns {boolean}
 */
export function requiresProjectAssignment(role) {
  return ROLES[role]?.requiresProjectAssignment || false;
}

/**
 * Get role display info
 * @param {string} roleId - Role ID
 * @returns {object} Role metadata
 */
export function getRoleInfo(roleId) {
  return ROLES[roleId] || null;
}

/**
 * Get all available roles (excluding admin which is the owner)
 * @returns {object[]} Array of role objects
 */
export function getInvitableRoles() {
  return Object.values(ROLES).filter(role => role.id !== 'admin');
}

export default {
  ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  PERMISSION_LABELS,
  PERMISSION_CATEGORIES,
  hasPermission,
  hasProjectAccess,
  getUserPermissions,
  requiresProjectAssignment,
  getRoleInfo,
  getInvitableRoles
};
